<script setup lang="ts">
import { onMounted, ref, nextTick } from 'vue';
import * as Sentry from '@sentry/vue';
import Loading from '@/components/ui/Loading.vue';
import { storeToRefs } from 'pinia';
import { useFrankieOneStore } from '@/stores/frankie-one.store';
import { useRouter } from 'vue-router';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import { wellKnownKycSessionRoutes } from '@/routes/wellKnownKycSessionRoutes';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { redirectToTimeoutWithSessionId } from '@/util/timeoutRedirector';
import { useFrankieOneIDVEvent } from '@/components/session/kyc/idv/FrankieOneIDVComposable';
import type { IdvResult } from '@/integrations/FrankieOne/types';
import DocumentVerificationIssue from '@/components/session/kyc/idv/DocumentVerificationIssue.vue';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';
import { ImmersveError } from '@/common/ImmersveError';
import type { OneSdkErrorEvent } from '@/components/session/kyc/idv/OneSdkErrorEvent';

const ONESDK_RETRY_LIMIT = 2;

const loading = ref(true);
const verificationFailure = ref(false);
const unsupportedDocument = ref(false);
const oneSdkReloadCount = ref(0);
const frankieOneStore = useFrankieOneStore();
const { kycSession } = storeToRefs(useKycSessionStore());
const router = useRouter();

const { idv } = storeToRefs(frankieOneStore);

async function redirectToTimeout() {
  await router.push(redirectToTimeoutWithSessionId({ kycSession: kycSession.value }));
}

async function reloadIdv() {
  verificationFailure.value = false;
  unsupportedDocument.value = false;
  loading.value = true;
  await nextTick(loadIdv);
}

async function resultReceived(payload: IdvResult & { isComplete?: boolean }) {
  if (payload.isComplete) {
    loading.value = false;
    await router.push({ name: wellKnownKycSessionRoutes.PendingReview });
  }
}

function setVerificationFailure() {
  verificationFailure.value = true;
}

function handleUnsupportedDocument() {
  unsupportedDocument.value = true;
  setVerificationFailure();
}

function setLoading(state: boolean) {
  loading.value = state;
}

async function errorReceived(payload: OneSdkErrorEvent) {
  if (isOneSdkLoadingRetryable(payload)) {
    oneSdkReloadCount.value++;
    Sentry.captureMessage('OneSdk failed to load. Retrying...', {
      extra: { payload, reloadAttempt: oneSdkReloadCount.value },
    });
    return await reloadIdv();
  }

  if (hasOneSdkFailedMounting(payload?.message)) {
    handleMountError(payload.message);
    return;
  }

  const error = ImmersveError.fromOneSdkError(payload);
  Sentry.captureException(error, {
    extra: { payload, reloadAttempt: oneSdkReloadCount.value },
  });
  await redirectToTimeout();
}

async function sessionInterrupted() {
  const steps = kycSession.value?.steps;
  const previousRoute = steps?.getPreviousScreen(router.currentRoute.value.name as string);

  await router.replace({ name: previousRoute ?? wellKnownKycSessionRoutes.Initiation });
}

function hasOneSdkFailedLoading(message: string) {
  return message?.includes('failed loading');
}

function hasOneSdkFailedMounting(message?: string) {
  return message && message.includes('DOM Element #idv-el not found');
}

async function startIdv() {
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }
  await frankieOneStore.loadOneSdk(kycSession.value);
  if (!idv.value) {
    Sentry.captureException({
      message: 'no frankie one idv found',
    });
    await redirectToTimeout();
    return;
  }

  const {
    onReady,
    onResults,
    onSessionDataFailedLoading,
    onError,
    onInputRequired,
    onUnsupportedDocument,
    onSessionInterrupted,
    onLoading,
    onDetectionComplete,
    onDetectionFailed,
  } = useFrankieOneIDVEvent(idv.value);

  onReady(() => setLoading(false));
  onResults(resultReceived);
  onSessionDataFailedLoading(redirectToTimeout);
  onError(errorReceived);
  onInputRequired(setVerificationFailure);
  onUnsupportedDocument(handleUnsupportedDocument);
  onDetectionFailed(setVerificationFailure);
  onSessionInterrupted(sessionInterrupted);
  onLoading((loadingState) => loadingState && setLoading(true));
  onDetectionComplete(() => setLoading(true));

  loadIdv();
}

function loadIdv() {
  loading.value = false;
  idv.value?.mount('#idv-el');
}

function handleMountError(message?: string) {
  const currentRouteName = router.currentRoute.value.name;
  Sentry.captureMessage('Error mounting FrankieOne IDV', {
    extra: {
      message,
      currentRouteName,
    },
  });
  if (currentRouteName === wellKnownKycSessionRoutes.Scans) {
    setVerificationFailure();
  }
}

function isOneSdkLoadingRetryable(payload: OneSdkErrorEvent) {
  return (
    payload.message &&
    hasOneSdkFailedLoading(payload.message) &&
    oneSdkReloadCount.value < ONESDK_RETRY_LIMIT
  );
}

onMounted(async () => {
  try {
    if (!kycSession.value?.region) {
      throw ImmersveError.create({
        name: 'KycSessionRegionNotFound',
        message: 'Kyc Session region is missing',
      });
    }
    if (
      !kycSession.value.cardholder.hasConsentForRegion({
        kycSessionId: kycSession.value.id,
        region: kycSession.value.region,
      })
    ) {
      return await router.replace({
        name: wellKnownKycSessionRoutes.Consent,
      });
    }

    if (!(await kycSession.value.cardholder.hasResidentialAddress())) {
      Sentry.captureMessage(
        `No residential address found for cardholder ${kycSession.value.cardholder.accountId}`
      );
      return await router.replace({
        name: wellKnownKycSessionRoutes.Address,
      });
    }
    //if this errors then the component catch all is triggered
    await kycSession.value?.pushExternalProfile();
    await startIdv();
  } catch (error: unknown) {
    if (error instanceof Error) {
      const err = ImmersveError.fromCause({
        name: 'FrankieOneIdvInitializationError',
        cause: error,
      });
      Sentry.captureException(err, {
        extra: { error },
      });
    }
    await redirectToTimeout();
  }
});

defineExpose({
  resultReceived,
  setVerificationFailure,
  handleUnsupportedDocument,
  errorReceived,
});
</script>

<template>
  <KycSessionLayout>
    <Loading class="mx-auto mt-10 h-10 w-10" v-show="loading" />
    <div class="w-full" data-test="screen-kyc-session" v-show="!verificationFailure && !loading">
      <div
        id="idv-el"
        class="relative mx-auto mb-16 w-full sm:mb-4 sm:w-[540px]"
        data-test="f1-idv-flow"
      ></div>
    </div>
    <div
      class="my-12 flex flex-col px-4"
      data-test="verification-failure-content"
      v-if="verificationFailure"
    >
      <DocumentVerificationIssue :unsupportedDocument="unsupportedDocument" />
    </div>
    <template #button-bar>
      <FlowNavigationButton
        text="Back"
        direction="back"
        v-show="!verificationFailure && !loading"
      />
      <button
        type="button"
        @click="reloadIdv"
        class="border-light-border text-light-primary outline-light-border dark:border-dark-brd-dark dark:text-dark-primary dark:outline-dark-brd-dark mx-6 my-4 w-full rounded-full border py-3 outline-2 sm:mx-0"
        data-test="try-again-button"
        v-if="verificationFailure"
      >
        Try Again
      </button>
    </template>
  </KycSessionLayout>
</template>

<style>
#idv-el {
  min-height: 70vh;
}

/* Incode */
#idv-el > iframe {
  margin: 0 auto !important;
  min-height: 100% !important;
}

/* Onfido */
#onfido-sdk {
  min-height: 70vh !important;
}
#onfido-sdk > iframe {
  max-height: 100% !important;
}
</style>
